import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GripVertical, Users, Phone, TrendingUp, ArrowLeft,
  RefreshCw, MoreHorizontal, Edit, Eye, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { getPipeline, updateLeadStage, syncLeads } from '../lib/crm-api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const STAGES = [
  { key: 'new', label: 'New', color: 'from-slate-500/20 to-slate-600/5', accent: 'bg-slate-500', text: 'text-slate-300', border: 'border-slate-500/30' },
  { key: 'contacted', label: 'Contacted', color: 'from-blue-500/20 to-blue-600/5', accent: 'bg-blue-500', text: 'text-blue-300', border: 'border-blue-500/30' },
  { key: 'interested', label: 'Interested', color: 'from-amber-500/20 to-amber-600/5', accent: 'bg-amber-500', text: 'text-amber-300', border: 'border-amber-500/30' },
  { key: 'qualified', label: 'Qualified', color: 'from-emerald-500/20 to-emerald-600/5', accent: 'bg-emerald-500', text: 'text-emerald-300', border: 'border-emerald-500/30' },
  { key: 'deal', label: 'Deal', color: 'from-primary/20 to-primary/5', accent: 'bg-primary', text: 'text-primary', border: 'border-primary/30' },
];

function ScoreIndicator({ score }) {
  const tier = score >= 70 ? 'hot' : score >= 40 ? 'warm' : 'cold';
  const colors = { hot: 'text-red-400', warm: 'text-amber-400', cold: 'text-blue-400' };
  const bgs = { hot: 'bg-red-500', warm: 'bg-amber-500', cold: 'bg-blue-500' };
  return (
    <div className="flex items-center gap-1.5">
      <div className={cn('w-1.5 h-1.5 rounded-full', bgs[tier])} />
      <span className={cn('text-xs font-mono', colors[tier])}>{score}</span>
    </div>
  );
}

function LeadCard({ lead, onDragStart, onDragEnd, navigate }) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ id: lead.id, stage: lead.pipeline_stage }));
        e.currentTarget.style.opacity = '0.5';
        onDragStart(lead.id);
      }}
      onDragEnd={(e) => {
        e.currentTarget.style.opacity = '1';
        onDragEnd();
      }}
      className="group bg-card/80 border border-border/50 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-primary/30 transition-all duration-200 hover:shadow-lg hover:shadow-primary/5"
      data-testid={`pipeline-card-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-semibold text-primary">{(lead.customer_name || lead.phone_number)?.[0]?.toUpperCase() || '?'}</span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{lead.customer_name || 'Unknown'}</p>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Phone className="w-2.5 h-2.5" />
              <span className="truncate">{lead.phone_number}</span>
            </div>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`pipeline-card-menu-${lead.id}`}>
              <MoreHorizontal className="w-3.5 h-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-card border-border">
            <DropdownMenuItem onClick={() => navigate('/crm')}><Edit className="w-3 h-3 mr-2" />Edit in CRM</DropdownMenuItem>
            {lead.conversation_id && <DropdownMenuItem onClick={() => navigate(`/conversations/${lead.conversation_id}`)}><Eye className="w-3 h-3 mr-2" />View Chat</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {lead.last_message && (
        <p className="text-[11px] text-muted-foreground mt-2 line-clamp-2 leading-relaxed">{lead.last_message}</p>
      )}

      <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-border/30">
        <ScoreIndicator score={lead.lead_score} />
        {lead.tags?.length > 0 && (
          <div className="flex gap-1">{lead.tags.slice(0, 2).map(t => <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted/60 text-muted-foreground">{t}</span>)}</div>
        )}
        {lead.message_count > 0 && (
          <span className="text-[10px] text-muted-foreground">{lead.message_count} msgs</span>
        )}
      </div>
    </div>
  );
}

export const PipelinePage = () => {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const loadPipeline = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getPipeline();
      setPipeline(res.data);
    } catch (err) {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPipeline(); }, [loadPipeline]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncLeads();
      toast.success(`Synced: ${res.data.created} new, ${res.data.updated} updated`);
      loadPipeline();
    } catch (err) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    try {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.stage === targetStage) return;

      // Optimistic update
      setPipeline(prev => {
        const updated = { ...prev };
        const sourceLeads = [...(updated[data.stage]?.leads || [])];
        const leadIdx = sourceLeads.findIndex(l => l.id === data.id);
        if (leadIdx === -1) return prev;

        const [movedLead] = sourceLeads.splice(leadIdx, 1);
        movedLead.pipeline_stage = targetStage;

        updated[data.stage] = { ...updated[data.stage], leads: sourceLeads, count: sourceLeads.length };
        updated[targetStage] = {
          ...updated[targetStage],
          leads: [...(updated[targetStage]?.leads || []), movedLead],
          count: (updated[targetStage]?.count || 0) + 1,
        };
        return updated;
      });

      await updateLeadStage(data.id, targetStage);
      toast.success(`Moved to ${STAGES.find(s => s.key === targetStage)?.label}`);
    } catch (err) {
      toast.error('Failed to move lead');
      loadPipeline(); // Revert on error
    }
  };

  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageKey);
  };

  return (
    <div className="p-6 space-y-6 h-full" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/crm')} data-testid="back-to-crm-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Pipeline</h1>
            <p className="text-muted-foreground mt-1">Drag and drop leads between stages</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={handleSync} disabled={syncing} data-testid="pipeline-sync-btn">
            <RefreshCw className={cn("w-4 h-4 mr-2", syncing && "animate-spin")} />{syncing ? 'Syncing...' : 'Sync'}
          </Button>
          <Button variant="secondary" onClick={loadPipeline} data-testid="pipeline-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Pipeline Board */}
      {loading ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map(s => (
            <div key={s.key} className="flex-shrink-0 w-[280px] h-[500px] rounded-xl bg-muted/20 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: 'calc(100vh - 220px)' }}>
          {STAGES.map((stage, idx) => {
            const stageData = pipeline[stage.key] || { leads: [], count: 0 };
            const isOver = dragOverStage === stage.key;
            return (
              <div
                key={stage.key}
                className={cn(
                  "flex-shrink-0 w-[280px] flex flex-col rounded-xl border transition-all duration-200",
                  isOver ? `${stage.border} bg-gradient-to-b ${stage.color} shadow-lg` : "border-border/40 bg-muted/5"
                )}
                onDragOver={(e) => handleDragOver(e, stage.key)}
                onDragLeave={() => setDragOverStage(null)}
                onDrop={(e) => handleDrop(e, stage.key)}
                data-testid={`pipeline-column-${stage.key}`}
              >
                {/* Column Header */}
                <div className="p-3 border-b border-border/30 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={cn("w-2.5 h-2.5 rounded-full", stage.accent)} />
                    <span className={cn("text-sm font-semibold", stage.text)}>{stage.label}</span>
                  </div>
                  <Badge variant="secondary" className="font-mono text-xs">{stageData.count}</Badge>
                </div>

                {/* Cards */}
                <ScrollArea className="flex-1 p-2">
                  <div className="space-y-2">
                    {stageData.leads.length === 0 ? (
                      <div className="py-8 text-center">
                        <Users className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                        <p className="text-xs text-muted-foreground/50">No leads</p>
                      </div>
                    ) : (
                      stageData.leads.map(lead => (
                        <LeadCard
                          key={lead.id}
                          lead={lead}
                          onDragStart={setDraggingId}
                          onDragEnd={() => setDraggingId(null)}
                          navigate={navigate}
                        />
                      ))
                    )}
                  </div>
                </ScrollArea>

                {/* Stage flow arrow */}
                {idx < STAGES.length - 1 && (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 hidden xl:block">
                    <ChevronRight className="w-4 h-4 text-muted-foreground/30" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
